import React from 'react'
import { useTheme } from '@mui/material'
import { LatLngExpression } from 'leaflet'
import { CircleMarker, Pane, Polyline } from 'react-leaflet'
import { IPosition } from 'src/generated-types'
import { MUIThemeColors } from 'src/mui-theme'
import { getThemeColor } from 'src/utils'
import { OlexMapHighlight as Highlight } from './olexMatch.action'

interface OlexMapHighlightProps {
  current: IPosition[]
  preview: IPosition[]
  highlight?: Highlight
}

const pointsOf = (position: IPosition): LatLngExpression[] => {
  const corner = position.connectedPositions?.[0]
  const anchor: LatLngExpression = [position.latitude, position.longitude]
  return corner ? [anchor, [corner.latitude, corner.longitude]] : [anchor, anchor]
}

// Draws the lines the user points at over the map: dashed where they are now, solid where the upload puts them.
const OlexMapHighlight: React.FC<OlexMapHighlightProps> = ({ current, preview, highlight }) => {
  const theme = useTheme()
  const now = getThemeColor(theme, MUIThemeColors.warningMain)
  const after = getThemeColor(theme, MUIThemeColors.primaryMain)
  const pick = (positions: IPosition[], ids: number[] = []) => positions.filter(p => ids.includes(p.id))
  const draw = (positions: IPosition[], color: string, dashed: boolean) =>
    positions.map(position => (
      <React.Fragment key={`${dashed ? 'now' : 'after'}-${position.id}`}>
        <Polyline
          positions={pointsOf(position)}
          pane="olexHighlight"
          pathOptions={{ color, weight: 6, opacity: 0.9, dashArray: dashed ? '8 8' : undefined }}
          interactive={false}
        />
        <CircleMarker
          center={[position.latitude, position.longitude]}
          radius={7}
          pane="olexHighlight"
          pathOptions={{ color, fillColor: color, fillOpacity: 1 }}
          interactive={false}
        />
      </React.Fragment>
    ))

  return (
    <Pane name="olexHighlight" style={{ zIndex: 630 }}>
      {draw(pick(current, highlight?.currentIds), now, true)}
      {draw(pick(preview, highlight?.previewIds), after, false)}
    </Pane>
  )
}

export default OlexMapHighlight
